import { ATTRIBUTE_GROUP_DELIMITER, GridParamType } from "constants/product-collection";
import { ValueOf } from "lib/ts-utility";
import lodashSet from "lodash/set";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import {
    GROUP_ID_DELIMITER,
    IServerDimensionPreference,
    IDimension,
    DimensionParamsActionType,
    DimensionProps,
    ExpandedColumnsProps,
    DimensionParamsAction,
    Order,
    IAllocationDimensionColumns,
    DimensionOrder,
    SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER,
    processActionsToDimensionParams,
} from "helpers/door-collection";
import { ColumnOrderTypes } from "../../product-collection-grid-view-helper";
import { DoorGroupMetric, DoorGroupMetrics, EditableMetrics, Metrics } from "helpers/metrics/types";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import isEqual from "lodash/isEqual";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { bootstrapDimensionsPanelState } from "reducers/dimensions-panel/helpers";
import { getLegacyTypeByMetricsKind } from "helpers/metrics/legacy-metrics";
import { SortModelItem } from "ag-grid-community";
import isEmpty from "lodash/isEmpty";
import { parseMetricSchema } from "reducers/dimensions-panel/draft";

export interface IBrowserPivot {
    mode?: boolean;
    columns?: string[];
}

export interface IServerPivot {
    mode: boolean;
    columns?: string[];
}

export interface IPreference {
    field_name: string;
    children?: IPreference[];
    display: {
        hidden: boolean;
    };
    grid_column: {
        width?: number; // applies to columns only
        expand?: boolean; // applies to groups only
    };
}

export interface IAggregationQueryParam {
    column: string;
    aggFunc: string;
}

export enum FilterTypes {
    Number = "number",
    Set = "set",
    Text = "text",
    Date = "date",
}

export enum Operators {
    And = "AND",
    Or = "OR",
}

export enum ISortOrder {
    Ascending = "ASCENDING",
    Descending = "DESCENDING",
}

export enum ISortSortBy {
    Column = "COLUMN",
    DoorMetrics = "DOOR_METRICS",
}

export interface IFilterConditionQueryParam {
    filterType: FilterTypes;
    type?: string;
    filter?: string | number;
    filterTo?: string | number;
    dateFrom?: string;
    dateTo?: string;
    values?: string[];
}

export interface IFilterOperatorQueryParam {
    filterType: FilterTypes;
    operator: Operators;
    condition1: IFilterConditionQueryParam;
    condition2: IFilterConditionQueryParam;
}

export interface IFiltersQueryParam {
    [colId: string]: IFilterConditionQueryParam | IFilterOperatorQueryParam;
}

export interface IGridParamsQueryParam {
    [GridParamType.Pivot]?: IBrowserPivot;
    [GridParamType.AggFunc]?: IAggregationQueryParam[];
    [GridParamType.RowGroup]?: string[];
    [GridParamType.Search]?: string;
    [GridParamType.Sort]?: Array<SortModelItem>;
    [GridParamType.PinnedRowSort]?: Array<{ id: string; order: ISortOrder; sort_by: ISortSortBy }>;
    [GridParamType.Filter]?: IFiltersQueryParam;
}

export interface IAggregation {
    column: string;
    function: string;
}

export interface ICondition {
    filter_type: FilterTypes;
    type: string;
    filter: string;
    filter_to: string;
    values: string[];
}

export interface IOperator {
    operator: Operators;
    conditions: ICondition[];
}

export interface IFilter {
    filter_on: string;
    filter: { operator: IOperator } | { condition: ICondition };
}

export interface ISort {
    id: string;
    order: ISortOrder;
    sort_by: ISortSortBy;
}

export interface IGridParams {
    aggregations: IAggregation[];
    row_groups: string[];
    pivot: IServerPivot;
    search_term: string;
    sort: ISort[];
    filters: Array<Partial<IFilter>>;
}
export interface IDisplayPreference {
    id?: string;
    name: string;
    display_preferences: IPreference[];
    grid_parameters: Partial<IGridParams>;
    allocation_dimensions?: IServerDimensionPreference;
    door_group_metrics?: DoorGroupMetrics;
}

export interface IMerchantDisplayPreference {
    id?: string;
    merchant_id?: string;
    name?: string;
    created_at?: number;
    created_by?: string;
    revision?: string;
    item_display_preference: IDisplayPreference;
}

export const transformConditionForUpdate = (condition: IFilterConditionQueryParam) => {
    const transformedCondition: ICondition = {
        filter_type: condition.filterType,
        type: "",
        filter: "",
        filter_to: "",
        values: [],
    };
    if (condition.filterType === FilterTypes.Date) {
        transformedCondition.type = condition.type;
        transformedCondition.filter = condition.dateFrom;
        if (condition.dateTo) {
            transformedCondition.filter_to = condition.dateTo;
        }
    } else if (condition.filterType === FilterTypes.Set) {
        transformedCondition.values = condition.values;
    } else {
        transformedCondition.type = condition.type;
        transformedCondition.filter =
            condition.filterType === FilterTypes.Number
                ? (condition.filter.toString() as string)
                : (condition.filter as string);
        if (condition.filterTo) {
            transformedCondition.filter_to =
                condition.filterType === FilterTypes.Number
                    ? (condition.filterTo.toString() as string)
                    : (condition.filterTo as string);
        }
    }
    return transformedCondition;
};

export const transformFiltersForUpdate = (filters: IFiltersQueryParam) => {
    const filterList = Object.keys(filters).map((colId) => {
        const filter = filters[colId];
        const transformedFilter: Partial<IFilter> = {
            filter_on: colId,
        };
        if ((filter as IFilterOperatorQueryParam).operator) {
            const condition1 = (filter as IFilterOperatorQueryParam).condition1;
            const condition2 = (filter as IFilterOperatorQueryParam).condition2;
            transformedFilter.filter = {
                operator: {
                    operator: (filter as IFilterOperatorQueryParam).operator,
                    conditions: [transformConditionForUpdate(condition1), transformConditionForUpdate(condition2)],
                },
            };
        } else {
            transformedFilter.filter = {
                condition: transformConditionForUpdate(filter as IFilterConditionQueryParam),
            };
        }
        return transformedFilter;
    });
    return filterList;
};

export const transformConditionForGrid = (condition: ICondition) => {
    const transformedCondition: IFilterConditionQueryParam = {
        filterType: condition.filter_type,
    };
    if (condition.filter_type === FilterTypes.Set) {
        transformedCondition.values = condition.values;
    } else if (condition.filter_type === FilterTypes.Date) {
        transformedCondition.type = condition.type;
        transformedCondition.dateFrom = condition.filter;
        if (condition.filter_to) {
            transformedCondition.dateTo = condition.filter_to;
        }
    } else {
        transformedCondition.type = condition.type;
        transformedCondition.filter =
            condition.filter_type === FilterTypes.Number ? parseFloat(condition.filter) : condition.filter;
        if (condition.filter_to) {
            transformedCondition.filterTo =
                condition.filter_type === FilterTypes.Number ? parseFloat(condition.filter_to) : condition.filter_to;
        }
    }
    return transformedCondition;
};

export const transformFiltersForGrid = (filters: Array<Partial<IFilter>>) => {
    const transformedFilters: IFiltersQueryParam = {};
    if (filters && filters.length) {
        filters.forEach((filterEntry) => {
            const { filter, filter_on } = filterEntry;
            if ((filter as { condition: ICondition }).condition) {
                transformedFilters[filter_on] = transformConditionForGrid(
                    (filter as { condition: ICondition }).condition,
                );
            } else if ((filter as { operator: IOperator }).operator) {
                const condition1 = transformConditionForGrid(
                    (filter as { operator: IOperator }).operator.conditions[0],
                );
                const condition2 = transformConditionForGrid(
                    (filter as { operator: IOperator }).operator.conditions[1],
                );
                transformedFilters[filter_on] = {
                    filterType: condition1.filterType,
                    operator: (filter as { operator: IOperator }).operator.operator,
                    condition1,
                    condition2,
                };
            }
        });
    }

    return transformedFilters;
};

export enum SaveStateToGridQueryParamsType {
    SetPivot = "setPivot",
    SetAggFunction = "setAggFunction",
    SetRowGroup = "setRowGroup",
    SetSearch = "setSearch",
    SetSort = "setSort",
    SetPinnedRowSort = "setPinnedRowSort",
    SetFilters = "setFilters",
}

// tslint:disable-next-line: interface-over-type-literal
export type SaveStateToGridQueryParamsAction = {
    [SaveStateToGridQueryParamsType.SetPivot]: {
        type: SaveStateToGridQueryParamsType.SetPivot;
        value: IBrowserPivot;
    };
    [SaveStateToGridQueryParamsType.SetAggFunction]: {
        type: SaveStateToGridQueryParamsType.SetAggFunction;
        value: IAggregationQueryParam[];
    };
    [SaveStateToGridQueryParamsType.SetRowGroup]: {
        type: SaveStateToGridQueryParamsType.SetRowGroup;
        value: string[];
    };
    [SaveStateToGridQueryParamsType.SetSearch]: {
        type: SaveStateToGridQueryParamsType.SetSearch;
        value: string;
    };
    [SaveStateToGridQueryParamsType.SetSort]: {
        type: SaveStateToGridQueryParamsType.SetSort;
        value: Array<{ colId: string; sort: string }>;
    };
    [SaveStateToGridQueryParamsType.SetPinnedRowSort]: {
        type: SaveStateToGridQueryParamsType.SetPinnedRowSort;
        value: Array<{ id: string; order: ISortOrder; sort_by: ISortSortBy }>;
    };
    [SaveStateToGridQueryParamsType.SetFilters]: {
        type: SaveStateToGridQueryParamsType.SetFilters;
        value: IFiltersQueryParam;
    };
};

export type ISaveStateToGridQueryParamsActions =
    | ValueOf<SaveStateToGridQueryParamsAction>
    | Array<ValueOf<SaveStateToGridQueryParamsAction>>;

export const saveStateToGridQueryParams = (
    saveStateToGridQueryParamsActions: ISaveStateToGridQueryParamsActions,
    existingState: IGridParamsQueryParam = {},
) => {
    const actions = Array.isArray(saveStateToGridQueryParamsActions)
        ? saveStateToGridQueryParamsActions
        : [saveStateToGridQueryParamsActions];
    let queryState: IGridParamsQueryParam = lodashCloneDeep(existingState);
    actions.forEach((action) => {
        switch (action.type) {
            case SaveStateToGridQueryParamsType.SetPivot:
                queryState = lodashSet(
                    queryState,
                    GridParamType.Pivot,
                    action.value && action.value.mode ? action.value : undefined,
                );
                break;
            case SaveStateToGridQueryParamsType.SetAggFunction:
                queryState = lodashSet(
                    queryState,
                    GridParamType.AggFunc,
                    action.value && action.value.length ? action.value : undefined,
                );
                break;
            case SaveStateToGridQueryParamsType.SetRowGroup:
                queryState = lodashSet(
                    queryState,
                    GridParamType.RowGroup,
                    action.value && action.value.length ? action.value : undefined,
                );
                break;
            case SaveStateToGridQueryParamsType.SetSearch:
                queryState = lodashSet(queryState, GridParamType.Search, action.value);
                break;
            case SaveStateToGridQueryParamsType.SetSort:
                queryState = lodashSet(
                    queryState,
                    GridParamType.Sort,
                    action.value && action.value.length ? action.value : undefined,
                );
                break;
            case SaveStateToGridQueryParamsType.SetPinnedRowSort:
                queryState = lodashSet(
                    queryState,
                    GridParamType.PinnedRowSort,
                    action.value && action.value.length ? action.value : undefined,
                );
                break;
            case SaveStateToGridQueryParamsType.SetFilters:
                queryState = lodashSet(
                    queryState,
                    GridParamType.Filter,
                    action.value && !isEmpty(action.value) ? action.value : undefined,
                );
                break;
            default:
                break;
        }
    });
    return queryState;
};

export const transformGridParamsForUpdate = (
    gridParams: IGridParamsQueryParam = {
        aggFunc: [],
        rowGroup: [],
        pivot: { mode: false, columns: [] },
        sort: [],
        pinnedRowSort: [],
        search: "",
        filter: {},
    },
): Partial<IGridParams> => {
    const {
        aggFunc: aggFuncs = [],
        rowGroup: row_groups = [],
        pivot = { mode: false, columns: [] },
        sort = [],
        pinnedRowSort = [],
        search = "",
        filter = {},
    } = gridParams;
    const aggregations = aggFuncs.map(({ column, aggFunc }) => ({ column, function: aggFunc }));
    const sortColumns = sort.map((sortColumn) => ({
        id: sortColumn.colId,
        order: (sortColumn.sort === "asc" ? ISortOrder.Ascending : ISortOrder.Descending) as ISortOrder,
        sort_by: ISortSortBy.Column as ISortSortBy,
    }));
    const filtersForUpdate = transformFiltersForUpdate(filter);
    return {
        aggregations,
        row_groups,
        pivot: {
            ...pivot,
            mode: pivot && pivot.mode ? true : false,
        },
        sort: sortColumns.concat(pinnedRowSort),
        search_term: search,
        filters: filtersForUpdate,
    };
};

export const transformGridParamsForGrid = (gridParams: IGridParams): IGridParamsQueryParam => {
    const { aggregations, row_groups, pivot, sort, filters } = gridParams;
    const actions = [
        {
            type: SaveStateToGridQueryParamsType.SetPivot,
            value: {
                ...pivot,
                mode: pivot && pivot.mode,
            },
        },
        {
            type: SaveStateToGridQueryParamsType.SetAggFunction,
            value: (aggregations ?? []).map<IAggregationQueryParam>((aggregation) => ({
                column: aggregation.column,
                aggFunc: aggregation.function,
            })),
        },
        {
            type: SaveStateToGridQueryParamsType.SetRowGroup,
            value: row_groups,
        },
        {
            type: SaveStateToGridQueryParamsType.SetSort,
            value: (sort || [])
                .filter((sortColumn) => sortColumn.sort_by === ISortSortBy.Column)
                .map((sortColumn) => ({
                    colId: sortColumn.id,
                    sort: sortColumn.order === ISortOrder.Ascending ? ColumnOrderTypes.Asc : ColumnOrderTypes.Desc,
                })),
        },
        {
            type: SaveStateToGridQueryParamsType.SetPinnedRowSort,
            value: (sort || [])
                .filter((sortColumn) => sortColumn.sort_by === ISortSortBy.DoorMetrics)
                .map((sortColumn) => ({
                    id: sortColumn.id,
                    order: sortColumn.order,
                })),
        },
        {
            type: SaveStateToGridQueryParamsType.SetFilters,
            value: transformFiltersForGrid(filters),
        },
    ] as Array<ValueOf<SaveStateToGridQueryParamsAction>>;
    return saveStateToGridQueryParams(actions);
};

/**
 * Check if dimension is the lowest dimension
 * @param index dimension index
 * @param dimensions stored dimensions preferences
 * @returns
 */
const isLowestDimension = (index: number, dimensions: Array<string | IDimension>): boolean => {
    return index === dimensions.length - 1;
};

export const transformDimensionOrderToGridOrder = (dimensionOrder: DimensionOrder): Order | undefined => {
    const map = {
        [DimensionOrder.Ascending]: Order.Ascending,
        [DimensionOrder.Descending]: Order.Descending,
        [DimensionOrder.Custom]: Order.Custom,
    };

    return map[dimensionOrder];
};

export const transformGridOrderToServer = (order: Order): DimensionOrder | undefined => {
    const map = {
        [Order.Ascending]: DimensionOrder.Ascending,
        [Order.Descending]: DimensionOrder.Descending,
        [Order.Custom]: DimensionOrder.Custom,
    };

    return map[order];
};

export const transformAllocationDimensionForUpdate = (
    dimensionParams: DimensionsPanelReduxState,
    defaultAllocationDimensions: string[],
): IServerDimensionPreference => {
    const dimensions = defaultAllocationDimensions.map((dimEntryId, index) => {
        const dim = {
            [DimensionProps.Id]: dimEntryId,
            [DimensionProps.ActiveValues]: {
                values: undefined,
                order: undefined,
                all_values: undefined,
            },
        };

        dim[DimensionProps.ActiveValues] = {
            values: dimensionParams.dimensions[index].values.reduce((acc, dimensionValue) => {
                if (dimensionValue.selected) {
                    acc.push({ id: dimensionValue.id });
                }
                return acc;
            }, []),
            order: transformGridOrderToServer(dimensionParams.dimensions[index].sort),
            all_values: false,
        };

        if (dim[DimensionProps.ActiveValues].values.length === 0) {
            // When none is selected, we want to save as `null`.
            dim[DimensionProps.ActiveValues] = null;
        } else if (dim[DimensionProps.ActiveValues].values.length === dimensionParams.dimensions[index].values.length) {
            // When everything is selected, set `all_values` to true
            dim[DimensionProps.ActiveValues].all_values = true;

            // When select all, we can store null only when order is not custom (asc/dec).
            // Doing this, all values will be selected, even new ones that will be added.
            if (dimensionParams.dimensions[index].sort !== Order.Custom) {
                dim[DimensionProps.ActiveValues].values = null;
            }
        }

        if (isLowestDimension(index, defaultAllocationDimensions)) {
            const activeAttributesObject = {
                attributes: [],
                order: DimensionOrder.Ascending,
            };

            const activeAttributeString = [];
            for (const attribute of dimensionParams.attributes) {
                if (!attribute.selected) {
                    continue;
                }

                const activeAttributeValues = attribute.values.filter((attributeValue) => attributeValue.selected);
                activeAttributeString.push(
                    `${attribute.id}${SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER}${attribute.sort}`,
                );

                const activeAttribute = {
                    field_name: attribute.id,
                    values: activeAttributeValues.map((val) => {
                        return { string_value: val.id };
                    }),
                    order: transformGridOrderToServer(attribute.sort),
                };

                if (activeAttribute.values.length === 0) {
                    // When none is selected, we want to save as `undefined`, so we skip the loop.
                    continue;
                } else if (activeAttribute.values.length === attribute.values.length) {
                    // When everything is selected, we want to save as null.
                    activeAttribute.values = null;
                }

                activeAttributesObject.attributes.push(activeAttribute);
            }

            if (activeAttributesObject.attributes.length > 0) {
                // Legacy. Combine active attributes into string
                dim[DimensionProps.Classification] = activeAttributeString.join(ATTRIBUTE_GROUP_DELIMITER);
                dim[DimensionProps.ActiveAttributes] = activeAttributesObject;
            }

            const singleActiveAttribute =
                activeAttributesObject.attributes.length === 1 &&
                dimensionParams.attributes.find((attribute) => attribute.selected);
            const singleActiveAttributeLabel =
                activeAttributesObject.attributes.length === 1 &&
                dimensionParams.attributes.find((attribute) => attribute.showLabelInHeader);

            dim[DimensionProps.ClassificationLabel] = singleActiveAttributeLabel
                ? singleActiveAttributeLabel.id
                : undefined;
            // Legacy. Only support single attribute sort
            dim[DimensionProps.ClassificationDir] = singleActiveAttribute ? singleActiveAttribute.sort : undefined;
            // Legacy. Only support values visibility of single attribute
            dim[DimensionProps.ClassificationHide] = singleActiveAttribute?.values?.reduce((acc, attributeValue) => {
                if (!attributeValue.selected) {
                    acc.push(attributeValue.id);
                }
                return acc;
            }, []);

            dim[DimensionProps.Show] = dimensionParams.showDoors;
            // Legacy. Support for legacy metrics
            dim[DimensionProps.PlanMetrics] = dimensionParams.metrics.showAssortmentPlan
                ? dimensionParams.metrics.values.map((metric) => getLegacyTypeByMetricsKind(metric.kind))
                : undefined;
            dim[DimensionProps.ShowTotals] = dimensionParams.metrics.showAssortmentTotals;
            dim[DimensionProps.ShowPlan] = dimensionParams.metrics.showAssortmentPlan;
        }
        return dim;
    });

    const expandedColumns = {
        delimiter: GROUP_ID_DELIMITER,
        columns: dimensionParams.collapsedGroupIds.reduce((acc, groudIdValue) => {
            acc[groudIdValue] = false;
            return acc;
        }, {}),
    };
    return {
        dimensions,
        expanded_columns: expandedColumns,
    };
};

export const transformAllocationDimensionForGrid = ({
    dimensionPreferences,
    allocationDimensions = [],
    metrics = [],
}: {
    dimensionPreferences: IServerDimensionPreference | undefined;
    allocationDimensions: IAllocationDimension[];
    metrics?: Metrics;
}): DimensionsPanelReduxState => {
    const dimensionIds = [];
    const dimensionActiveValues: IAllocationDimensionColumns = [];
    const delimiter = lodashGet(
        dimensionPreferences,
        `expanded_columns.${ExpandedColumnsProps.Delimiter}`,
        GROUP_ID_DELIMITER,
    );
    const dimensions = lodashGet(dimensionPreferences, "dimensions");
    const actions = [
        {
            type: DimensionParamsActionType.SetDelimiter,
            value: delimiter,
        },
    ] as Array<ValueOf<DimensionParamsAction>>;

    // Pre-Macy's merchants always have deliveries and doors shown.
    // Thus, we had never stored visibility of individual delivery or door.
    // Since Macy's, we introduce more granular configuration that allows storing of individual delivery and door.

    // For backward compatibility, TRUE `granularActiveValues` flag will handle visibility granularly.
    // FALSE `granularActiveValues` flag will not handle visibility of individual delivery and door
    const granularActiveValues = dimensions?.some((dimension) => !!dimension[DimensionProps.ActiveValues]);

    dimensions?.forEach((dimension, index) => {
        if (granularActiveValues) {
            let values = [];
            let order = undefined;

            if (dimension[DimensionProps.ActiveValues]) {
                const activeValues = dimension[DimensionProps.ActiveValues].values || [];

                values = activeValues.length > 0 ? activeValues.map((val) => val.id) : null;
                // `all_values` boolean forces all values to be selected. With exception of custom order
                if (
                    dimension[DimensionProps.ActiveValues].all_values &&
                    dimension[DimensionProps.ActiveValues].order !== DimensionOrder.Custom
                ) {
                    // `null` values is considered select all
                    values = null;
                }

                order = dimension[DimensionProps.ActiveValues].order;
            }

            dimensionActiveValues[index] = {
                dimensionId: dimension[DimensionProps.Id],
                values,
                direction: transformDimensionOrderToGridOrder(order),
            };
        }

        dimensionIds.push(dimension[DimensionProps.Id]);
        if (isLowestDimension(index, dimensions)) {
            const activeAttributeNames = [];
            const activeAttributesObject = [];
            const activeAttributes = lodashGet(dimension, DimensionProps.ActiveAttributes);

            (activeAttributes?.attributes ?? []).forEach((activeAttribute) => {
                const activeAttributeString = `${
                    activeAttribute.field_name
                }${SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER}${transformDimensionOrderToGridOrder(
                    activeAttribute.order,
                )}`;
                activeAttributeNames.push(activeAttributeString);
                activeAttributesObject.push({
                    field: activeAttribute.field_name,
                    values: activeAttribute.values?.map((val) => val.string_value) ?? null,
                    direction: transformDimensionOrderToGridOrder(activeAttribute.order),
                });
            });

            (
                [
                    {
                        type: DimensionParamsActionType.ShowDoorsGroup,
                        value: !!lodashGet(dimension, DimensionProps.Show),
                    },
                    {
                        type: DimensionParamsActionType.ShowClassificationLabel,
                        value: lodashGet(dimension, DimensionProps.ClassificationLabel),
                    },
                    !granularActiveValues && {
                        type: DimensionParamsActionType.ChangeDirection,
                        value: lodashGet(dimension, DimensionProps.ClassificationDir),
                    },
                    !granularActiveValues && {
                        type: DimensionParamsActionType.HideClassification,
                        value: lodashGet(dimension, DimensionProps.ClassificationHide),
                    },
                    {
                        type: DimensionParamsActionType.SelectPlanMetric,
                        value: lodashGet(dimension, DimensionProps.PlanMetrics),
                    },
                    {
                        type: DimensionParamsActionType.ShowDoorTotals,
                        value: lodashGet(dimension, DimensionProps.ShowTotals),
                    },
                    {
                        type: DimensionParamsActionType.ShowPlanMetrics,
                        value: lodashGet(dimension, DimensionProps.ShowPlan),
                    },
                ] as Array<ValueOf<DimensionParamsAction>>
            ).forEach((action) => {
                if (action) {
                    actions.push(action);
                }
            });

            if (activeAttributeNames.length > 0) {
                // Store active attributes and sort
                actions.push({
                    type: DimensionParamsActionType.ShowClassification,
                    value: activeAttributeNames.join(","),
                });

                // Store values of attributes
                actions.push({
                    type: DimensionParamsActionType.SetActiveAttributes,
                    value: activeAttributesObject,
                });
            } else {
                actions.push({
                    type: DimensionParamsActionType.ShowClassification,
                    value: lodashGet(dimension, DimensionProps.Classification),
                });
            }
        }
    });
    if (
        !isEmpty(dimensionIds) &&
        !isEqual(
            dimensionIds,
            allocationDimensions.map((dim) => dim.id),
        )
    ) {
        actions.push({
            type: DimensionParamsActionType.SetAllocationIds,
            value: dimensionIds,
        });
    }
    if (dimensionActiveValues.length > 0) {
        actions.push({
            type: DimensionParamsActionType.SetActiveValues,
            value: dimensionActiveValues,
        });
    }
    const columns = lodashGet(dimensionPreferences, `expanded_columns.${ExpandedColumnsProps.Columns}`, {});
    Object.keys(columns).forEach((colName) => {
        actions.push({
            type: columns[colName] ? DimensionParamsActionType.ExpandGroup : DimensionParamsActionType.CollapseGroup,
            value: colName,
        });
    });
    if (metrics && metrics.length) {
        actions.push({
            type: DimensionParamsActionType.SetMetrics,
            value: metrics,
        });
    }
    return processActionsToDimensionParams(
        actions,
        bootstrapDimensionsPanelState({
            allocationDimensions,
            initialMetrics: metrics,
        }),
    );
};

export const transformDoorGroupMetricsForUpdate = (metrics?: Metrics): DoorGroupMetrics => {
    if (!metrics) {
        return null;
    }

    return metrics.reduce((acc, metric) => {
        if (metric) {
            acc.push({
                show_value: metric.showValue,
                show_totals: metric.showTotals,
                show_subtotals: metric.showSubtotals,
                display_name: metric.displayName,
                kind: metric.kind,
                read_only: !EditableMetrics.includes(metric.kind),
            });
        }
        return acc;
    }, []);
};

export const parseMetrics = (currentViewMetrics: DoorGroupMetric[], latestSchemaMetrics: DoorGroupMetric[]) => {
    if (isEmpty(currentViewMetrics)) {
        const parsedMetrics = parseMetricSchema(latestSchemaMetrics);
        return parsedMetrics;
    }

    // This is because some schemas are saved without some keys like `number_format`
    // or `target_view`, so we need to merge them with the latest schema structure
    const mergedMetrics = currentViewMetrics.reduce((acc, metric, i) => {
        const metricFromLatestShema = latestSchemaMetrics.find((m) => m.kind === metric.kind);
        if (!metricFromLatestShema) {
            return acc;
        }

        const { target_view, number_format } = metricFromLatestShema;
        const mergedMetric = {
            ...metric,
            ...(target_view && { target_view }),
            ...(number_format && { number_format }),
        };
        acc.push(mergedMetric);
        return acc;
    }, []);

    const parsedMetrics = parseMetricSchema(mergedMetrics);
    return parsedMetrics;
};
